import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    role: '',
  });
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    push_notifications: true,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [platformSettings, setPlatformSettings] = useState({});
  const [systemInfo, setSystemInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadProfile();
    if (activeTab === 'platform') {
      loadPlatformSettings();
    }
    if (activeTab === 'system') {
      loadSystemInfo();
    }
  }, [activeTab]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/settings/profile');
      if (response.data.status === 'success') {
        setProfile({
            username: response.data.data.username || response.data.data.name || '',
          email: response.data.data.email || '',
          role: response.data.data.role || '',
        });
        setNotifications({
          email_notifications: response.data.data.email_notifications,
          push_notifications: response.data.data.push_notifications,
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlatformSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/settings/platform');
      if (response.data.status === 'success') {
        setPlatformSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading platform settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSystemInfo = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/settings/system-info');
      if (response.data.status === 'success') {
        setSystemInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error loading system info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.put('/admin/settings/profile', {
        ...profile,
        ...notifications,
      });
      if (response.data.status === 'success') {
        setSuccess('Profile updated successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setError('New passwords do not match');
      return;
    }

    setIsSaving(true);

    try {
      const response = await api.put('/admin/settings/password', passwordData);
      if (response.data.status === 'success') {
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex  justify-content-between align-items-center mb-4">
        <h2 className="text-gray-900 font-weight-bold">Settings</h2>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <i className="fas fa-user mr-2"></i>Profile
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <i className="fas fa-lock mr-2"></i>Change Password
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'platform' ? 'active' : ''}`}
            onClick={() => setActiveTab('platform')}
          >
            <i className="fas fa-cogs mr-2"></i>Platform Settings
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            <i className="fas fa-info-circle mr-2"></i>System Info
          </button>
        </li>
      </ul>

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="row">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Profile Information</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleProfileSubmit}>
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                          <input
                              type="text"
                              className="form-control"
                              id="username"
                              value={profile.username}
                              onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                              required
                          />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          value={profile.email}
                          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="role">Role</label>
                        <input
                          type="text"
                          className="form-control"
                          id="role"
                          value="admin"
                          disabled
                        />
                      </div>

                      <hr />

                      <h6 className="mb-3">Notification Preferences</h6>

                      <div className="form-group">
                        <div className="custom-control custom-switch">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="emailNotifications"
                            checked={notifications.email_notifications}
                            onChange={(e) =>
                              setNotifications({
                                ...notifications,
                                email_notifications: e.target.checked,
                              })
                            }
                          />
                          <label className="custom-control-label" htmlFor="emailNotifications">
                            Email Notifications
                          </label>
                        </div>
                        <small className="form-text text-muted">
                          Receive notifications about platform activities via email
                        </small>
                      </div>

                      <div className="form-group">
                        <div className="custom-control custom-switch">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="pushNotifications"
                            checked={notifications.push_notifications}
                            onChange={(e) =>
                              setNotifications({
                                ...notifications,
                                push_notifications: e.target.checked,
                              })
                            }
                          />
                          <label className="custom-control-label" htmlFor="pushNotifications">
                            Push Notifications
                          </label>
                        </div>
                        <small className="form-text text-muted">
                          Receive real-time push notifications in the application
                        </small>
                      </div>

                      <button type="submit" className="btn btn-primary" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Profile'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="row">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Change Password</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="form-group">
                        <label htmlFor="currentPassword">Current Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="currentPassword"
                          value={passwordData.current_password}
                          onChange={(e) =>
                            setPasswordData({ ...passwordData, current_password: e.target.value })
                          }
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="newPassword">New Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="newPassword"
                          value={passwordData.new_password}
                          onChange={(e) =>
                            setPasswordData({ ...passwordData, new_password: e.target.value })
                          }
                          minLength={8}
                          required
                        />
                        <small className="form-text text-muted">
                          Password must be at least 8 characters long
                        </small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="confirmPassword"
                          value={passwordData.new_password_confirmation}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              new_password_confirmation: e.target.value,
                            })
                          }
                          minLength={8}
                          required
                        />
                      </div>

                      <button type="submit" className="btn btn-primary" disabled={isSaving}>
                        {isSaving ? 'Changing...' : 'Change Password'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Platform Settings Tab */}
          {activeTab === 'platform' && (
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Platform Settings</h5>
                  </div>
                  <div className="card-body">
                    {Object.keys(platformSettings).length === 0 ? (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle mr-2"></i>
                        No platform settings configured yet. Settings can be added through the database or API.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Group</th>
                              <th>Key</th>
                              <th>Value</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(platformSettings).map(([group, settings]) =>
                              settings.map((setting) => (
                                <tr key={setting.id}>
                                  <td>
                                    <span className="badge badge-primary">{group}</span>
                                  </td>
                                  <td><code>{setting.key}</code></td>
                                  <td>
                                    {typeof setting.value === 'boolean'
                                      ? setting.value
                                        ? 'Yes'
                                        : 'No'
                                      : setting.value}
                                  </td>
                                  <td>
                                    <small className="text-muted">{setting.description || '-'}</small>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* System Info Tab */}
          {activeTab === 'system' && (
            <div className="row">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">System Information</h5>
                  </div>
                  <div className="card-body">
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <th style={{ width: '30%' }}>PHP Version</th>
                          <td>{systemInfo.php_version || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Laravel Version</th>
                          <td>{systemInfo.laravel_version || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Database</th>
                          <td>{systemInfo.database || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Timezone</th>
                          <td>{systemInfo.timezone || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Environment</th>
                          <td>
                            <span
                              className={`badge ${
                                systemInfo.environment === 'production'
                                  ? 'badge-success'
                                  : 'badge-warning'
                              }`}
                            >
                              {systemInfo.environment || 'N/A'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Debug Mode</th>
                          <td>
                            <span
                              className={`badge ${
                                systemInfo.debug_mode ? 'badge-danger' : 'badge-success'
                              }`}
                            >
                              {systemInfo.debug_mode ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {systemInfo.environment === 'production' && systemInfo.debug_mode && (
                      <div className="alert alert-danger mt-3">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        <strong>Warning:</strong> Debug mode is enabled in production. This is a security risk and should be disabled.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSettings;
